import React, { useState, useEffect } from 'react';
import { AlertTriangle, CreditCard, X, MessageSquare } from 'lucide-react';

const SMSCreditWarning = ({ onClose }) => {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRefillModal, setShowRefillModal] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [requestSent, setRequestSent] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchBalance();
  }, []);

  const fetchBalance = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/sms-credits/balance`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setBalance(data);
      }
    } catch (e) {
      console.log('Could not fetch SMS balance');
    } finally {
      setLoading(false);
    }
  };

  const requestRefill = async () => {
    if (!selectedAmount) return;
    
    setRequesting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/sms-credits/request-refill`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ amount: selectedAmount })
      });
      
      if (res.ok) {
        setRequestSent(true);
      }
    } catch (e) {
      console.error('Failed to request refill:', e);
    } finally {
      setRequesting(false);
    }
  };

  if (loading || !balance) return null;
  
  // Don't show if balance is above threshold
  if (!balance.is_low) return null;

  return (
    <>
      {/* Warning Banner */}
      <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl p-4 mb-6 shadow-lg">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-lg">⚠️ Low SMS Credit Balance</h3>
              <p className="text-white/90 mt-1">
                Your SMS credit balance is running low. Please request a refill to continue sending SMS messages.
              </p>
              <button
                onClick={() => setShowRefillModal(true)}
                className="mt-3 px-4 py-2 bg-white text-orange-600 rounded-lg font-bold hover:bg-orange-50 transition-colors inline-flex items-center gap-2"
              >
                <CreditCard className="w-4 h-4" />
                Request SMS Credit Refill
              </button>
            </div>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-white/70 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Refill Request Modal */}
      {showRefillModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-black flex items-center gap-2">
                <CreditCard className="w-6 h-6 text-pink-600" />
                Request SMS Credit Refill
              </h2>
              <button onClick={() => setShowRefillModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            {requestSent ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-green-800 mb-2">Refill Request Sent!</h3>
                <p className="text-gray-600 mb-4">
                  Your request for <strong>${selectedAmount}</strong> credit has been submitted.
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  You will receive an invoice via email. Once payment is received, 
                  your SMS credits will be recharged and you'll be notified.
                </p>
                <button
                  onClick={() => {
                    setShowRefillModal(false);
                    setRequestSent(false);
                    setSelectedAmount(null);
                  }}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700"
                >
                  Got It!
                </button>
              </div>
            ) : (
              <>
                {/* Package Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-bold mb-3">Select Credit Amount</label>
                  <div className="grid grid-cols-3 gap-3">
                    {[25, 50, 75].map((amount) => (
                      <button
                        key={amount}
                        onClick={() => setSelectedAmount(amount)}
                        className={`p-4 rounded-xl border-2 text-center transition-all ${
                          selectedAmount === amount
                            ? 'border-pink-500 bg-pink-50 text-pink-700'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <span className="text-2xl font-black">${amount}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Info */}
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-6">
                  <p className="text-sm text-blue-800">
                    <strong>How it works:</strong> After submitting your request, 
                    you'll receive an invoice. Once payment is confirmed, your 
                    account will be recharged and you can continue sending SMS messages.
                  </p>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowRefillModal(false)}
                    className="flex-1 px-4 py-3 border-2 rounded-lg font-bold hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={requestRefill}
                    disabled={!selectedAmount || requesting}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg font-bold hover:from-pink-600 hover:to-purple-600 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {requesting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4" />
                        Request ${selectedAmount || '...'} Credit
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SMSCreditWarning;
